import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  askDialogueAgain,
  updateBook,
  updateDialogue,
  type Characters,
  type Dialogue,
} from "../api";
import { Amount } from "../components/Amount";
import MutationError from "../components/MutationError";
import { useT } from "../i18n";
import { aroundAmount } from "./money";

/**
 * DialogueSection is who says each quoted line (ADR-0123, requirement 0056),
 * on the book's Characters screen: what the last run found and cost, and the
 * "N chapters changed" press. The lines themselves are shown and corrected in
 * the chapter's own text, an avatar before each quote (ADR-0125).
 *
 * Nothing labels a book until the author presses (ADR-0182): extraction,
 * translation and refresh write the cast and stop, because every cast needs a
 * review first. On a book with no chapter labelled, the press reads as the
 * first labelling — "Find who says what · N chapters" — not as "changed".
 *
 * Nothing runs on save — autosave writes a revision mid-sentence — so after
 * edits the author presses Update, which asks only about the paragraphs that
 * changed. On a linked book too: a label is not a roster write.
 *
 * "Ask again" asks the whole book once more — after the brief improved, or a
 * character gained a form — keeping the author's corrections (ADR-0161).
 * Nothing asks again on its own.
 *
 * Finding the speakers the cast lacks (ADR-0169) has no button here: it is a
 * press the lead makes through the API (ADR-0171). Its run shows like any
 * other, with how many speakers it kept.
 *
 * A one-speaker book (ADR-0180) shows only that: every line is the narrator's,
 * so there is no run to show, no estimate and no press — the server refuses
 * every one.
 *
 * The model that finds who says each line is the book's choice (ADR-0181):
 * pills, never a dropdown, from the server's list. Choosing one starts nothing
 * and spends nothing; the next run uses it, and the estimates beside the
 * presses are already its prices.
 */
export default function DialogueSection({
  bookID,
  roster,
  canSpend,
  onRoster,
}: {
  bookID: string;
  roster: Characters;
  /** book.augment: the update press spends, and correcting takes the same. */
  canSpend: boolean;
  onRoster: (next: Characters) => void;
}) {
  const t = useT();
  const client = useQueryClient();
  const d = roster.dialogue;
  const run = d.run;
  const running = run?.status === "running";

  const onStarted = (next: Characters) => {
    onRoster(next);
    void client.invalidateQueries({ queryKey: ["dialogue"] });
    void client.invalidateQueries({ queryKey: ["usage"] });
  };
  const update = useMutation({ mutationFn: () => updateDialogue(bookID), onSuccess: onStarted });
  const again = useMutation({ mutationFn: () => askDialogueAgain(bookID), onSuccess: onStarted });

  // No model can be called on this server at all: nothing to choose between.
  if (!d.models.some((m) => m.available)) {
    return run ? null : (
      <p className="mt-3 text-xs text-muted" data-testid="dialogue-unavailable">
        {t.dialogue.unavailable}
      </p>
    );
  }
  // Nothing to say until the book has a cast. A cast with no run yet — every
  // book after its extraction (ADR-0182) — is shown the press that starts the
  // first run; hiding it until a run existed left such books with no way in
  // (2026-09-24).
  if (!run && d.attributed === 0 && roster.characters.length === 0) return null;

  if (d.one_speaker) {
    const [before, after] = aroundAmount(t.dialogue.oneSpeaker);
    return (
      <section className="mt-6" data-testid="dialogue" data-status="one-speaker">
        <h2 className="text-sm font-medium">{t.dialogue.heading}</h2>
        <p
          className="mt-1 max-w-prose text-xs leading-5 text-ink-soft"
          data-testid="dialogue-one-speaker"
        >
          {before}
          <bdi dir="auto">{d.one_speaker.name}</bdi>
          {after}
        </p>
      </section>
    );
  }

  const lines = Object.values(d.labels).reduce((a, b) => a + b, 0);
  // No chapter labelled yet: the press is the book's first labelling, which
  // nothing starts but the author, after reviewing the cast (ADR-0182 §3).
  // The server's count decides it, never the client's.
  const first = d.attributed === 0;

  return (
    <section className="mt-6" data-testid="dialogue" data-status={run?.status ?? "none"}>
      <h2 className="text-sm font-medium">{t.dialogue.heading}</h2>
      <p className="mt-1 max-w-prose text-xs leading-5 text-muted">{t.dialogue.note}</p>
      <LabelModelChips bookID={bookID} dialogue={d} canChoose={canSpend} />
      {!d.available && (
        <p
          className="mt-2 rounded-control bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          data-testid="dialogue-model-unavailable"
        >
          {t.dialogue.modelUnavailable}
          {d.reason && <span dir="auto"> {d.reason}</span>}
        </p>
      )}
      <div className="mt-2 space-y-1 text-xs text-muted">
        {running && run && (
          <p data-testid="dialogue-progress">
            {t.dialogue.progress(
              Math.min(run.chapter_done + run.chapter_failed + 1, run.chapter_total),
              run.chapter_total,
            )}
          </p>
        )}
        {!running && d.attributed > 0 && run?.status !== "refused" && (
          <p data-testid="dialogue-result" className="text-ink-soft">
            <Amount
              sentence={(a) => t.dialogue.result(lines, d.attributed, a)}
              amount={run?.cost_display ?? ""}
            />
          </p>
        )}
        {run?.status === "refused" && (
          <p
            data-testid="dialogue-refused"
            className="rounded-control bg-amber-50 px-3 py-2 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          >
            {t.dialogue.refused}
          </p>
        )}
        {run?.status === "partial" && (
          <p data-testid="dialogue-partial">{t.dialogue.partial(run.chapter_failed)}</p>
        )}
        {run?.status === "failed" && (
          <p role="alert" data-testid="dialogue-failed" className="text-red-600 dark:text-red-400">
            {t.dialogue.failed}
            {run.error && <span dir="auto"> {run.error}</span>}
          </p>
        )}
        {!running && d.changed > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p data-testid="dialogue-changed" data-first={first || undefined}>
              <Amount
                sentence={(a) => (first ? t.dialogue.first(a) : t.dialogue.changed(d.changed, a))}
                amount={d.estimate_display}
              />
            </p>
            {canSpend && d.available && (
              <button
                type="button"
                data-testid="dialogue-update"
                disabled={update.isPending}
                onClick={() => update.mutate()}
                className="rounded-full border border-sage px-2.5 py-0.5 text-xs font-medium text-sage-deep transition-colors hover:bg-sage-soft disabled:opacity-40"
              >
                {update.isPending
                  ? t.dialogue.updating
                  : first
                    ? t.dialogue.find(d.changed)
                    : t.dialogue.update}
              </button>
            )}
          </div>
        )}
        {!running && d.attributed > 0 && canSpend && d.available && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p data-testid="dialogue-again-cost">
              <Amount sentence={(a) => t.dialogue.again(a)} amount={d.again_display} />
            </p>
            <button
              type="button"
              data-testid="dialogue-again"
              disabled={again.isPending || update.isPending}
              onClick={() => again.mutate()}
              className="rounded-full border border-sage px-2.5 py-0.5 text-xs font-medium text-sage-deep transition-colors hover:bg-sage-soft disabled:opacity-40"
            >
              {again.isPending ? t.dialogue.askingAgain : t.dialogue.askAgain}
            </button>
          </div>
        )}
        {!running && run?.trigger === "speakers" && run.status !== "refused" && (
          <p data-testid="dialogue-speakers-found">{t.dialogue.speakersFound(run.added)}</p>
        )}
        <MutationError
          testId="dialogue-again-error"
          error={again.error}
          fallback={t.dialogue.againFailed}
        />
        <MutationError
          testId="dialogue-update-error"
          error={update.error}
          fallback={t.dialogue.updateFailed}
        />
      </div>
    </section>
  );
}

/**
 * LabelModelChips chooses the book's label model (ADR-0181). The chip lit is
 * the model the book resolves to — its choice or the house default — and a
 * model this server cannot call is shown but cannot be chosen.
 */
function LabelModelChips({
  bookID,
  dialogue,
  canChoose,
}: {
  bookID: string;
  dialogue: Dialogue;
  canChoose: boolean;
}) {
  const t = useT();
  const client = useQueryClient();
  const choose = useMutation({
    mutationFn: (model: string) => updateBook(bookID, { label_model: model }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["characters", bookID] });
      void client.invalidateQueries({ queryKey: ["book", bookID] });
    },
  });
  return (
    <div className="mt-3" data-testid="label-models">
      <p className="text-xs text-muted">{t.dialogue.modelHeading}</p>
      <div className="mt-1 flex flex-wrap gap-2">
        {dialogue.models.map((m) => {
          const chosen = m.id === dialogue.model;
          return (
            <button
              key={m.id}
              type="button"
              data-testid={`label-model-${m.id.split("/")[0]}`}
              data-model={m.id}
              aria-pressed={chosen}
              title={m.available ? undefined : m.reason}
              disabled={!canChoose || !m.available || choose.isPending}
              onClick={() => {
                if (!chosen) choose.mutate(m.id);
              }}
              className={`rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-40 ${
                chosen ? "border-sage bg-sage-soft font-medium" : "border-line hover:bg-sunk"
              }`}
            >
              {m.name}
              {chosen && !dialogue.chosen && (
                <span className="text-muted"> · {t.dialogue.modelDefault}</span>
              )}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-muted" data-testid="label-models-note">
        {t.dialogue.modelNote}
      </p>
      <MutationError
        testId="label-model-error"
        error={choose.error}
        fallback={t.dialogue.modelFailed}
      />
    </div>
  );
}
